/**
 * Regras puras da Programação do portal (03/11/2026). Sem React, para o teste
 * alcançar sem montar tela.
 */
import dayjs from 'dayjs'
import type { CargaProgramada, Programacao } from './api'

export const MAX_CARGAS = 40
export const INTERVALOS = [30, 45, 60, 90, 120] as const

export const ordenar = (lista: string[]) => [...lista].sort()
export const INTERVALO_PADRAO = 60
export const INICIO_PADRAO = '07:00'
export const DIAS_NA_FAIXA = 7

/** Soma minutos a um HH:MM. Passar da meia-noite para em 23:59: a programação tem
 *  uma data só, e virar o dia escondido no horário enganaria o cliente. */
export function somarMinutos(hhmm: string, minutos: number): string {
  const [h, m] = hhmm.split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm
  const total = Math.min(h * 60 + m + minutos, 23 * 60 + 59)
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/** Os horários gerados pelo primeiro horário e pelo intervalo. */
export function gerarHorarios(inicio: string, intervalo: number, quantidade: number): string[] {
  return Array.from({ length: quantidade }, (_, i) => somarMinutos(inicio, i * Math.max(0, intervalo)))
}

/**
 * Muda a quantidade sem mexer no que já está na grade: a carga nova entra depois
 * da última, no intervalo, e a que sai é a do fim.
 */
export function redimensionar(atuais: string[], quantidade: number, inicio: string, intervalo: number): string[] {
  if (quantidade <= atuais.length) return atuais.slice(0, Math.max(0, quantidade))
  const out = [...atuais]
  while (out.length < quantidade) {
    const ultimo = out[out.length - 1]
    out.push(ultimo ? somarMinutos(ultimo, Math.max(0, intervalo)) : inicio)
  }
  return out
}

/** O intervalo que a grade existente sugere, para a reprogramação continuar nele. */
export function intervaloSugerido(horarios: string[]): number {
  const ord = ordenar(horarios)
  if (ord.length < 2) return INTERVALO_PADRAO
  const [h1, m1] = ord[0].split(':').map(Number)
  const [h2, m2] = ord[1].split(':').map(Number)
  const diff = h2 * 60 + m2 - (h1 * 60 + m1)
  return diff > 0 ? diff : INTERVALO_PADRAO
}


export interface DiaDaFaixa {
  iso: string
  /** Hoje, Amanhã, ou o dia da semana. */
  rotulo: 'hoje' | 'amanha' | 'semana'
  diaMes: string
}

export function proximosDias(hoje: string, n = DIAS_NA_FAIXA): DiaDaFaixa[] {
  const base = dayjs(hoje)
  return Array.from({ length: n }, (_, i) => {
    const d = base.add(i, 'day')
    return { iso: d.format('YYYY-MM-DD'), rotulo: i === 0 ? 'hoje' : i === 1 ? 'amanha' : 'semana', diaMes: d.format('DD/MM') }
  })
}

export type EstadoDaProgramacao = 'enviada' | 'reprogramada' | 'em_atendimento'

export function estadoDaProgramacao(p: Programacao): EstadoDaProgramacao {
  if (p.cargas.some((c) => c.travada)) return 'em_atendimento'
  if (p.reprogramada) return 'reprogramada'
  return 'enviada'
}

/** Chave i18n do andamento da carga que já começou. */
export function andamentoDaCarga(c: CargaProgramada): string {
  switch (c.trip_status) {
    case 'carregando': return 'carregando'
    case 'transito': return 'transito'
    case 'descarregando': return 'descarregando'
    case 'atendido': return 'atendida'
    case 'nao_atendido': return 'naoAtendida'
    default:
      return c.status === 'loading' ? 'carregando' : c.status === 'released' ? 'atendida' : 'emAndamento'
  }
}

export function faixaDeHorarios(lista: (string | null)[]): { primeiro: string; ultimo: string } | null {
  const validos = lista.filter((h): h is string => Boolean(h)).sort()
  if (validos.length === 0) return null
  return { primeiro: validos[0], ultimo: validos[validos.length - 1] }
}

/**
 * O que muda na reprogramação, para o quadro de confirmação. Compara as listas
 * ordenadas: o horário que some de um lado e aparece no outro é destaque.
 */
export function diferenca(antes: string[], depois: string[]): { saiu: Set<string>; entrou: Set<string> } {
  const restoAntes = [...antes]
  const entrou = new Set<string>()
  for (const h of depois) {
    const i = restoAntes.indexOf(h)
    if (i >= 0) restoAntes.splice(i, 1)
    else entrou.add(h)
  }
  return { saiu: new Set(restoAntes), entrou }
}

export const horarioValido = (h: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(h)
